import { setAllInfluenced, updateInfluence } from './influence';
import { setDefaultValuesToCombinations } from './combinations';
import { getCombinationsErrors } from '../validations/variations';
import { replaceOperation } from '../product/operations';
import { editVariations } from '../edit/variations';
import { getSize } from '../iterable/getSize';

import { COMBINATIONS, COMBINATION_ATTRIBUTES_TO_RETAIN, INFLUENCE, INFLUENCE_ATTRIBUTES, RETAIN, RETAIN_ATTRIBUTES, VARIANTS } from '../../constants/variations';
import { ATTRIBUTE_DEFAULTS, DEFAULT_VALUES, ERROR, VALUE } from '../../constants/product';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';
import ATTRIBUTE from '../../constants/attributes';

function isEmptyValue({ attribute, channel, value }) {
  function isNull(value) {
    return value === null;
  }

  function isNullOrZero(value) {
    return value === null || Number(value) === 0;
  }

  switch (channel) {
    case ETSY: {
      switch (attribute) {
        case ATTRIBUTE.PRICE:
        case ATTRIBUTE.QUANTITY: {
          return isNullOrZero(value);
        }

        default: {
          return isNull(value);
        }
      }
    }

    case SHOPIFY: {
      switch (attribute) {
        case ATTRIBUTE.CAP:
        case ATTRIBUTE.CPI:
        case ATTRIBUTE.PRICE:
        case ATTRIBUTE.QUANTITY:
        case ATTRIBUTE.WEIGHT: {
          return isNullOrZero(value);
        }

        case ATTRIBUTE.UNIT: {
          return value === ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT];
        }

        default: {
          return isNull(value);
        }
      }
    }

    default: {
      return false;
    }
  }
}

function setDefaultValues({ channel, product, variations }) {
  function setDefaultValue(attributes) {
    return function map(source) {
      let combination = source;

      for (const attribute in attributes) {
        if (attributes.hasOwnProperty(attribute)) {
          if (isEmptyValue({ attribute, channel, value: combination.get(attribute) })) {
            combination = combination.set(attribute, attributes[attribute]);
          }
        }
      }

      return combination;
    };
  }

  switch (channel) {
    case ETSY: {
      const attributes = {
        [ATTRIBUTE.PRICE]: product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]),
        [ATTRIBUTE.QUANTITY]: product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]),
        [ATTRIBUTE.SKU]: product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]),
      };

      const combinations = variations.get(COMBINATIONS).map(setDefaultValue(attributes));
      return variations.set(COMBINATIONS, combinations);
    }

    case SHOPIFY: {
      const combinations = variations.get(COMBINATIONS).map(setDefaultValue(ATTRIBUTE_DEFAULTS[channel]));
      return variations.set(COMBINATIONS, combinations);
    }

    default: {
      return variations;
    }
  }
}

export function retainPhotos(props) {
  let { productOptions, profileOptions } = props;

  function retainPhoto(profileOption, sourceIndex) {
    const value = profileOption.get('value');

    function findSameOption(productOption) {
      return productOption.get('value') === value;
    }

    const sameOptionIndex = productOptions.findIndex(findSameOption);

    if (sameOptionIndex > -1) {
      const hash = productOptions.getIn([sameOptionIndex, 'imageHash'], null);
      profileOptions = profileOptions.setIn([sourceIndex, 'imageHash'], hash);
      productOptions = productOptions.splice(sameOptionIndex, 1);
    }
  }

  profileOptions.forEach(retainPhoto);

  return profileOptions;
}

function retainEtsyVariations({ product, variations }) {
  const channel = ETSY;

  function reduceVariants(result, variant, variantIndex) {
    return result && variant.get('name') === product.getIn([FORM.VARIATIONS, VARIANTS, variantIndex, 'name']);
  }

  const sameVariants = variations.get(VARIANTS).reduce(reduceVariants, true);

  if (!sameVariants) return setDefaultValues({ channel, product, variations });

  function reduceInfluence(result, attribute) {
    if (
      getSize(result.influence.get(attribute)) === 0 &&
      getSize(product.getIn([FORM.VARIATIONS, INFLUENCE, attribute])) > 0
    ) {
      result.attributesToRetain = result.attributesToRetain.add(attribute);
      result.influence = result.influence.set(attribute, product.getIn([FORM.VARIATIONS, INFLUENCE, attribute]));
    }

    return result;
  }

  const { attributesToRetain, influence } = INFLUENCE_ATTRIBUTES[channel].reduce(
    reduceInfluence,
    { attributesToRetain: EMPTY.SET, influence: variations.get(INFLUENCE) }
  );

  function mapVariants(source, variantIndex) {
    let variant = source;
    const productVariant = product.getIn([FORM.VARIATIONS, VARIANTS, variantIndex]);

    if (productVariant) {
      const profileOptions = variant.get('options');
      const productOptions = productVariant.get('options');
      variant = variant.set('options', retainPhotos({ profileOptions, productOptions }));
    }

    return variant;
  }

  function mapCombinations(combination, combinationIndex) {
    function reduceAttributes(result, attribute) {
      function isMatching(productCombination) {
        function reduceOptions(match, option, optionIndex) {
          return match && (
            !product.getIn([FORM.VARIATIONS, INFLUENCE, attribute, optionIndex]) ||
            option.get('name') === combination.getIn(['options', optionIndex, 'name'])
          );
        }

        return productCombination.get('options').reduce(reduceOptions, true);
      }

      const sameIndexCombination = product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]);
      const productCombination = sameIndexCombination && isMatching(sameIndexCombination, combinationIndex)
        ? sameIndexCombination
        : product.getIn([FORM.VARIATIONS, COMBINATIONS]).find(isMatching);

      const combinationValue = productCombination
        ? productCombination.get(attribute)
        : product.getIn([FORM.INVENTORY, attribute, VALUE]);

      return result.set(attribute, combinationValue);
    }

    return attributesToRetain.reduce(reduceAttributes, combination);
  }

  const variants = variations.get(VARIANTS).map(mapVariants);

  const combinations = getSize(attributesToRetain)
    ? variations.get(COMBINATIONS).map(mapCombinations)
    : variations.get(COMBINATIONS);

  return setAllInfluenced(
    variations
      .set(INFLUENCE, influence)
      .set(COMBINATIONS, combinations)
      .set(VARIANTS, variants)
  );
}

function retainShopifyVariations({ product, variations }) {
  const channel = SHOPIFY;

  function mapCombinations(combination, combinationIndex) {
    function shouldRetainValue(attribute) {
      switch (attribute) {
        case 'imageId':
        case 'imageHash':
        case ATTRIBUTE.CHARGE_TAX:
        case ATTRIBUTE.CONTINUE_SELLING: {
          return true;
        }

        case ATTRIBUTE.PHYSICAL: {
          return (
            !combination.get(ATTRIBUTE.PHYSICAL) || (
              isEmptyValue({
                attribute: ATTRIBUTE.COUNTRY_CODE,
                channel,
                value: combination.get(ATTRIBUTE.COUNTRY_CODE),
              }) &&
              isEmptyValue({ attribute: ATTRIBUTE.HS_CODE, channel, value: combination.get(ATTRIBUTE.HS_CODE) }) &&
              isEmptyValue({ attribute: ATTRIBUTE.WEIGHT, channel, value: combination.get(ATTRIBUTE.WEIGHT) }) &&
              isEmptyValue({ attribute: ATTRIBUTE.UNIT, channel, value: combination.get(ATTRIBUTE.UNIT) })
            )
          );
        }

        case ATTRIBUTE.TRACK_QUANTITY: {
          return isEmptyValue({ attribute: ATTRIBUTE.QUANTITY, channel, value: combination.get(ATTRIBUTE.QUANTITY) });
        }

        default: {
          return isEmptyValue({ attribute, channel, value: combination.get(attribute) });
        }
      }
    }

    function reduceAttributes(result, attribute) {
      if (!shouldRetainValue(attribute)) return result;

      function isMatching(productOffering) {
        function reduceOptions(match, option, optionIndex) {
          return match && option.get('name') === combination.getIn(['options', optionIndex, 'name']);
        }

        return productOffering.get('options').reduce(reduceOptions, true);
      }

      const sameIndexCombination = product.getIn([FORM.VARIATIONS, COMBINATIONS, combinationIndex]);
      const productCombination = sameIndexCombination && isMatching(sameIndexCombination)
        ? sameIndexCombination
        : product.getIn([FORM.VARIATIONS, COMBINATIONS]).find(isMatching);

      const combinationValue = productCombination
        ? productCombination.get(attribute)
        : attribute in ATTRIBUTE_DEFAULTS[channel]
          ? ATTRIBUTE_DEFAULTS[channel][attribute]
          : null;

      return result.set(attribute, combinationValue);
    }

    return COMBINATION_ATTRIBUTES_TO_RETAIN[channel].reduce(reduceAttributes, combination);
  }

  return updateInfluence({
    channel,
    variations: variations.set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations)),
  });
}

export function retain({ channel, product, validate = true, variations }) {
  switch (channel) {
    case ETSY: {
      const retained = retainEtsyVariations({ product, variations });
      return validate
        ? retained.set(ERROR, getCombinationsErrors({ channel, variations: retained }))
        : retained;
    }

    case SHOPIFY: {
      const retained = retainShopifyVariations({ product, variations });
      return validate
        ? retained.set(ERROR, getCombinationsErrors({ channel, variations: retained }))
        : retained;
    }

    default: {
      return variations;
    }
  }
}

export function getRetainFromVariations({ channel, variations }) {
  function canRetainEtsyAttribute(attribute) {
    return getSize(variations.getIn([INFLUENCE, attribute])) === 0;
  }

  function canRetainShopifyAttribute({ attribute, combination }) {
    switch (attribute) {
      case ATTRIBUTE.PRICE: {
        return (
          isEmptyValue({ attribute: ATTRIBUTE.CAP, channel, value: combination.get(ATTRIBUTE.CAP) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.CPI, channel, value: combination.get(ATTRIBUTE.CPI) }) &&
          isEmptyValue({ attribute: ATTRIBUTE.PRICE, channel, value: combination.get(ATTRIBUTE.PRICE) })
        );
      }

      case ATTRIBUTE.QUANTITY: {
        return (
          !combination.get(ATTRIBUTE.TRACK_QUANTITY) ||
          isEmptyValue({ attribute: ATTRIBUTE.QUANTITY, channel, value: combination.get(ATTRIBUTE.QUANTITY) })
        );
      }

      case ATTRIBUTE.SHIPPING: {
        return (
          !combination.get(ATTRIBUTE.PHYSICAL) || (
            isEmptyValue({
              attribute: ATTRIBUTE.COUNTRY_CODE,
              channel,
              value: combination.get(ATTRIBUTE.COUNTRY_CODE),
            }) &&
            isEmptyValue({ attribute: ATTRIBUTE.HS_CODE, channel, value: combination.get(ATTRIBUTE.HS_CODE) }) &&
            isEmptyValue({ attribute: ATTRIBUTE.WEIGHT, channel, value: combination.get(ATTRIBUTE.WEIGHT) }) &&
            isEmptyValue({ attribute: ATTRIBUTE.UNIT, channel, value: combination.get(ATTRIBUTE.UNIT) })
          )
        );
      }

      default: {
        return isEmptyValue({ attribute, channel, value: combination.get(attribute) });
      }
    }
  }

  function reduceEtsyAttributes(result, attribute) {
    return result.get(attribute) === false
      ? result
      : result.set(attribute, canRetainEtsyAttribute(attribute));
  }

  function reduceShopifyAttributes(result, attribute) {
    if (result.retain.get(attribute) !== false) {
      result.retain = result.retain.set(attribute,
        canRetainShopifyAttribute({ attribute, combination: result.combination })
      );
    }

    return result;
  }

  function reduceShopifyCombinations(result, combination) {
    return RETAIN_ATTRIBUTES[SHOPIFY].reduce(reduceShopifyAttributes, { combination, retain: result }).retain;
  }

  switch (channel) {
    case ETSY: {
      if (getSize(variations.get(VARIANTS)) === 0) return EMPTY.MAP;

      return RETAIN_ATTRIBUTES[channel].reduce(reduceEtsyAttributes, EMPTY.MAP);
    }

    case SHOPIFY: {
      return variations.get(COMBINATIONS).reduce(reduceShopifyCombinations, EMPTY.MAP);
    }

    default: {
      return EMPTY.MAP;
    }
  }
}

export function updateTemplateForRetain({ channel, template }) {
  if (getSize(template.getIn([FORM.VARIATIONS, RETAIN])) === 0) return template;

  function getUpdatedTemplate() {
    function reduceEtsyAttributes(source, attribute) {
      if (!template.getIn([FORM.VARIATIONS, RETAIN, attribute])) return source;

      const variations = setAllInfluenced(source.get(FORM.VARIATIONS).setIn([INFLUENCE, attribute], EMPTY.MAP));
      const result = source.set(FORM.VARIATIONS, setDefaultValuesToCombinations({ attribute, variations }));

      switch (attribute) {
        case ATTRIBUTE.SKU: {
          // BE set product level SKU to variations, the null also should be there
          return result.setIn([FORM.INVENTORY, attribute], DEFAULT_VALUES.NULL);
        }

        default: {
          return result;
        }
      }
    }

    function reduceShopifyAttributes(result, attribute) {
      if (!template.getIn([FORM.VARIATIONS, RETAIN, attribute])) return result;

      switch (attribute) {
        case ATTRIBUTE.PRICE: {
          return result
            .set(ATTRIBUTE.CAP, null)
            .set(ATTRIBUTE.CHARGE_TAX, true)
            .set(ATTRIBUTE.CPI, null)
            .set(ATTRIBUTE.PRICE, null);
        }

        case ATTRIBUTE.QUANTITY: {
          return result
            .set(ATTRIBUTE.CONTINUE_SELLING, false)
            .set(ATTRIBUTE.TRACK_QUANTITY, false)
            .set(ATTRIBUTE.QUANTITY, null);
        }

        case ATTRIBUTE.SHIPPING: {
          return result
            .set(ATTRIBUTE.PHYSICAL, false)
            .set(ATTRIBUTE.COUNTRY_CODE, null)
            .set(ATTRIBUTE.HS_CODE, null)
            .set(ATTRIBUTE.WEIGHT, null)
            .set(ATTRIBUTE.UNIT, ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT]);
        }

        default: {
          return result.set(attribute, null);
        }
      }
    }

    function mapShopifyCombinations(combination) {
      return RETAIN_ATTRIBUTES[SHOPIFY].reduce(reduceShopifyAttributes, combination);
    }

    switch (channel) {
      case ETSY: {
        return RETAIN_ATTRIBUTES[channel].reduce(reduceEtsyAttributes, template);
      }

      case SHOPIFY: {
        const combinations = template.getIn([FORM.VARIATIONS, COMBINATIONS]).map(mapShopifyCombinations);
        return template.setIn([FORM.VARIATIONS, COMBINATIONS], combinations);
      }

      default: {
        return template;
      }
    }
  }

  const product = getUpdatedTemplate();
  const updates = editVariations({ channel, product, value: product.get(FORM.VARIATIONS) });
  return updates.product.set('operations',
    updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
  );
}
